import { useQuery } from "@tanstack/react-query";
import cytoscape, { type Core } from "cytoscape";
import fcose from "cytoscape-fcose";
import { useMemo, useRef, useState } from "react";
import CytoscapeComponent from "react-cytoscapejs";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api.ts";

cytoscape.use(fcose);

interface GraphNode {
	id: string;
	type: string;
	label: string;
	layer: string;
}
interface GraphEdge {
	id: string;
	source: string;
	target: string;
	rel: string;
}
interface GraphData {
	nodes: GraphNode[];
	edges: GraphEdge[];
}
interface Labels {
	rels: Array<{ rel: string; count: number }>;
	nodeCounts: Array<{ type: string; count: number }>;
}

// Colour per node type (message/domain visible layer vs infra).
const TYPE_COLOR: Record<string, string> = {
	message: "#2563eb",
	domain: "#7c3aed",
	host: "#0891b2",
	ip: "#059669",
	nameserver: "#d97706",
	dkim_selector: "#db2777",
	esp: "#dc2626",
	asn: "#0d9488",
	registrar: "#6366f1",
};

export function GraphView({ focusDomain, focusBrand }: { focusDomain?: string; focusBrand?: string }) {
	const navigate = useNavigate();
	const cyRef = useRef<Core | null>(null);
	const [enabledRels, setEnabledRels] = useState<Set<string> | null>(null);
	const [selected, setSelected] = useState<GraphNode | null>(null);

	const labels = useQuery({ queryKey: ["graph-labels"], queryFn: () => api.get<Labels>("/api/graph/labels") });
	const query = focusBrand
		? `?focusBrand=${encodeURIComponent(focusBrand)}`
		: focusDomain
			? `?focus=${encodeURIComponent(focusDomain)}`
			: "";
	const graph = useQuery({
		queryKey: ["graph", focusBrand ? `brand:${focusBrand}` : focusDomain],
		queryFn: () => api.get<GraphData>(`/api/graph${query}`),
	});

	const rels = useMemo(() => enabledRels ?? new Set((labels.data?.rels ?? []).map((r) => r.rel)), [
		enabledRels,
		labels.data,
	]);

	const elements = useMemo(() => {
		if (!graph.data) return [];
		const edges = graph.data.edges.filter((e) => rels.has(e.rel));
		const keep = new Set<string>();
		for (const e of edges) {
			keep.add(e.source);
			keep.add(e.target);
		}
		const nodeEls = graph.data.nodes
			.filter((n) => keep.has(n.id) || !edges.length)
			.map((n) => ({ data: { id: n.id, label: n.label, type: n.type, layer: n.layer } }));
		const edgeEls = edges.map((e) => ({
			data: { id: e.id, source: e.source, target: e.target, label: e.rel },
		}));
		return [...nodeEls, ...edgeEls];
	}, [graph.data, rels]);

	// Layout is driven by CytoscapeComponent's `layout` prop (it runs it on the
	// live instance at the right lifecycle point). A fresh object whenever the
	// visible element count changes makes it re-run after filtering.
	// biome-ignore lint/correctness/useExhaustiveDependencies: keyed on element count
	const layoutOpts = useMemo(
		() => ({
			name: "fcose",
			animate: false,
			randomize: true,
			fit: true,
			padding: 40,
			// After positioning, clamp the fit zoom so a sparse graph doesn't
			// magnify labels, then re-center.
			stop: () => {
				const cy = cyRef.current;
				if (!cy) return;
				try {
					if (cy.zoom() > 1.1) {
						cy.zoom(1.1);
						cy.center();
					}
				} catch {
					// ignore
				}
			},
		}),
		[elements.length],
	);

	const stylesheet = useMemo(
		() => [
			{
				selector: "node",
				style: {
					"background-color": (n: cytoscape.NodeSingular) => TYPE_COLOR[n.data("type")] ?? "#64748b",
					label: "data(label)",
					"font-size": "8px",
					color: "#111827",
					"text-wrap": "ellipsis",
					"text-max-width": "90px",
					width: 16,
					height: 16,
				},
			},
			{
				selector: "edge",
				style: {
					width: 1,
					"line-color": "#cbd5e1",
					"target-arrow-color": "#cbd5e1",
					"target-arrow-shape": "triangle",
					"arrow-scale": 0.6,
					"curve-style": "bezier",
					label: "data(label)",
					"font-size": "6px",
					color: "#94a3b8",
				},
			},
			{ selector: "node:selected", style: { "border-width": 3, "border-color": "#111827" } },
		],
		[],
	);

	return (
		<div className="flex gap-3" style={{ height: "calc(100vh - 180px)", minHeight: 440 }}>
			{/* Rel filters */}
			<div className="w-48 shrink-0 overflow-auto rounded-md border border-line bg-surface p-3">
				<div className="text-[11px] uppercase text-muted mb-2">Relationships</div>
				{(labels.data?.rels ?? []).map((r) => (
					<label key={r.rel} className="flex items-center gap-2 text-xs text-content mb-1 cursor-pointer">
						<input
							type="checkbox"
							checked={rels.has(r.rel)}
							onChange={(e) => {
								const next = new Set(rels);
								if (e.target.checked) next.add(r.rel);
								else next.delete(r.rel);
								setEnabledRels(next);
							}}
						/>
						{r.rel} <span className="text-muted">({r.count})</span>
					</label>
				))}
			</div>

			{/* Canvas */}
			<div className="flex-1 rounded-md border border-line bg-surface overflow-hidden">
				{graph.isLoading ? (
					<div className="p-4 text-muted">Loading…</div>
				) : (
					<CytoscapeComponent
						elements={elements}
						layout={layoutOpts}
						stylesheet={stylesheet as never}
						style={{ width: "100%", height: "100%" }}
						cy={(cy: Core) => {
							cyRef.current = cy;
							// Cap zoom so a sparse graph's fit doesn't blow the labels up.
							cy.maxZoom(1.5);
							cy.minZoom(0.15);
							cy.removeListener("tap");
							cy.on("tap", "node", (evt) => {
								const d = evt.target.data();
								setSelected({ id: d.id, type: d.type, label: d.label, layer: d.layer });
							});
						}}
					/>
				)}
			</div>

			{/* Detail panel */}
			{selected && (
				<div className="w-56 shrink-0 overflow-auto rounded-md border border-line bg-surface p-3">
					<div className="text-[11px] uppercase text-muted">{selected.type}</div>
					<div className="text-sm text-content font-medium break-all mb-2">{selected.label}</div>
					{selected.type === "domain" && (
						<button
							type="button"
							className="text-primary text-xs hover:underline"
							onClick={() => navigate(`/domains/${encodeURIComponent(selected.label)}`)}
						>
							Open domain →
						</button>
					)}
					{selected.type === "message" && (
						<button
							type="button"
							className="text-primary text-xs hover:underline"
							onClick={() => navigate(`/messages/${selected.id.split(":")[1]}`)}
						>
							Open case →
						</button>
					)}
				</div>
			)}
		</div>
	);
}
